import * as React from 'react';

import {
  enumInventoryMovementSortOption,
  InventoryTransactionType,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  Checkbox,
  ConsoleDialog,
  Loading,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { humanReadableShortPricingUnit } from '@rematter/ui-web/lib';
import { useAtomValue, useSetAtom } from 'jotai';
import { loadable } from 'jotai/utils';
import { LucidePlus } from 'lucide-react';

import {
  allocateToLotAtom,
  LotAllocationInput,
} from '../../../_graphql/mutationInventoryLot';
import {
  MovementForAllocation,
  movementsForAllocationQueryAtom,
} from '../../../_graphql/queryMovementsForAllocation';
import { useTranslations } from '../../../i18n/useTranslations';

import { MovementAllocationRow } from './MovementAllocationRow';

function formatTransactionType(
  type: InventoryTransactionType,
  t: ReturnType<typeof useTranslations>,
): string {
  return t.movementHistoryView.transactionTypes[type] || type;
}

function getShortTransactionId(originTransactionItemId: string): string | null {
  const colonIndex = originTransactionItemId.indexOf(':');
  const parentId =
    colonIndex > 0
      ? originTransactionItemId.substring(0, colonIndex)
      : originTransactionItemId;

  const prefixes = ['pulo_', 'load_'];
  for (const prefix of prefixes) {
    if (parentId.startsWith(prefix)) {
      return parentId.slice(prefix.length, prefix.length + 8);
    }
  }
  return null;
}

type AllocationQuantity = {
  movementId: string;
  quantity: number;
};

type AddMovementsToLotDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  lotId: string;
  facilityId: string;
  onSuccess?: () => void;
};

export const AddMovementsToLotDialog = ({
  isOpen,
  onClose,
  lotId,
  facilityId,
  onSuccess,
}: AddMovementsToLotDialogProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedMovementIds, setSelectedMovementIds] = React.useState<
    Set<string>
  >(new Set());
  const [allocations, setAllocations] = React.useState<AllocationQuantity[]>(
    [],
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const allocateToLot = useSetAtom(allocateToLotAtom({}));

  const $movementsQuery = React.useMemo(
    () =>
      loadable(
        movementsForAllocationQueryAtom(() => ({
          variables: {
            facilityId,
            limit: 100,
            offset: 0,
            sorting: [enumInventoryMovementSortOption.OccurredAtDesc],
          },
        })),
      ),
    [facilityId],
  );

  const movementsResult = useAtomValue($movementsQuery);
  const isLoading = movementsResult.state === 'loading';
  const hasError = movementsResult.state === 'hasError';
  const queryResult =
    movementsResult.state === 'hasData' ? movementsResult.data : null;
  const movements = queryResult?.data?.movements ?? [];

  const filteredMovements = React.useMemo(() => {
    if (!movements.length) return [];

    if (!searchTerm.trim()) return movements;

    const term = searchTerm.toLowerCase();
    return movements.filter(
      (m: MovementForAllocation) =>
        m.itemName.toLowerCase().includes(term) || m.id.includes(term),
    );
  }, [movements, searchTerm]);

  const handleClose = React.useCallback(() => {
    setSearchTerm('');
    setSelectedMovementIds(new Set());
    setAllocations([]);
    onClose();
  }, [onClose]);

  const handleToggleMovement = React.useCallback(
    (movement: MovementForAllocation) => {
      setSelectedMovementIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(movement.id)) {
          newSet.delete(movement.id);
          setAllocations((allocs) =>
            allocs.filter((a) => a.movementId !== movement.id),
          );
        } else {
          newSet.add(movement.id);
          setAllocations((allocs) => {
            if (allocs.some((a) => a.movementId === movement.id)) {
              return allocs;
            }
            return [
              ...allocs,
              {
                movementId: movement.id,
                quantity: movement.unallocatedQuantity,
              },
            ];
          });
        }
        return newSet;
      });
    },
    [],
  );

  const handleQuantityChange = React.useCallback(
    (movementId: string, quantity: number) => {
      setAllocations((prev) =>
        prev.map((a) => (a.movementId === movementId ? { ...a, quantity } : a)),
      );
    },
    [],
  );

  const handleRemoveMovement = React.useCallback((movementId: string) => {
    setSelectedMovementIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(movementId);
      return newSet;
    });
    setAllocations((prev) => prev.filter((a) => a.movementId !== movementId));
  }, []);

  const selectedMovements = React.useMemo(() => {
    if (!movements.length) return [];
    return movements.filter((m: MovementForAllocation) =>
      selectedMovementIds.has(m.id),
    );
  }, [movements, selectedMovementIds]);

  const validAllocations = React.useMemo(
    () => allocations.filter((a) => a.quantity > 0),
    [allocations],
  );

  const canSubmit = validAllocations.length > 0 && !isSubmitting;

  const handleSubmit = React.useCallback(async () => {
    if (validAllocations.length === 0) return;

    setIsSubmitting(true);

    try {
      const allocationInputs: LotAllocationInput[] = validAllocations.map(
        (alloc) => {
          const movement = selectedMovements.find(
            (m) => m.id === alloc.movementId,
          );
          return {
            movementId: alloc.movementId,
            quantity: {
              value: alloc.quantity,
              unit: movement?.quantityUnit || 'Pounds',
            },
          };
        },
      );

      await allocateToLot({
        lotId,
        allocations: allocationInputs,
      });

      toast.success(t.lotsListView.allocation.success);
      onSuccess?.();
      handleClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t.lotsListView.dialog.unknownError;
      toast.error(t.lotsListView.allocation.error + ': ' + errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validAllocations,
    selectedMovements,
    lotId,
    allocateToLot,
    toast,
    t,
    onSuccess,
    handleClose,
  ]);

  const formatMovementDate = React.useCallback((occurredAt: string) => {
    if (!occurredAt) return '-';
    const date = new Date(occurredAt.replace(/\[.*\]$/, ''));
    return isNaN(date.getTime())
      ? '-'
      : date.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        });
  }, []);

  return (
    <ConsoleDialog
      className="@AddMovementsToLotDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={t.lotsListView.allocation.addMovements}
      size="wrap"
      content={
        <div className="w-[600px] space-y-4">
          <TextField
            label={t.lotsListView.allocation.searchMovements}
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t.lotsListView.allocation.searchPlaceholder}
          />

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loading isLoading>
                <span className="text-neutral/60">
                  {t.lotsListView.allocation.loadingMovements}
                </span>
              </Loading>
            </div>
          ) : hasError ? (
            <div className="py-4 text-center text-sm text-failure">
              {t.lotsListView.allocation.errorLoadingMovements}
            </div>
          ) : (
            <>
              <div className="max-h-[200px] overflow-y-auto rounded-md border border-neutral/20">
                {filteredMovements.length === 0 ? (
                  <div className="py-4 text-center text-sm text-neutral/60">
                    {t.lotsListView.allocation.noEligibleMovements}
                  </div>
                ) : (
                  <div className="divide-y divide-neutral/10">
                    {filteredMovements.map((movement) => {
                      const isSelected = selectedMovementIds.has(movement.id);
                      return (
                        <div
                          key={movement.id}
                          className="flex items-center gap-3 p-2 hover:bg-neutral/5"
                        >
                          <Checkbox
                            isSelected={isSelected}
                            onChange={() => handleToggleMovement(movement)}
                          >
                            <div className="flex flex-1 flex-col gap-1 text-sm">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">
                                    {formatTransactionType(
                                      movement.originTransactionType,
                                      t,
                                    )}
                                    {getShortTransactionId(
                                      movement.originTransactionItemId,
                                    ) &&
                                      ` #${getShortTransactionId(movement.originTransactionItemId)}`}
                                  </span>
                                  <span className="text-neutral/50">•</span>
                                  <span>{movement.itemName}</span>
                                  {movement.ownerAccountName && (
                                    <>
                                      <span className="text-neutral/50">•</span>
                                      <span className="text-neutral/75">
                                        {movement.ownerAccountName}
                                      </span>
                                    </>
                                  )}
                                </div>
                                <span className="text-neutral/75">
                                  {movement.unallocatedQuantity}{' '}
                                  {humanReadableShortPricingUnit(
                                    movement.quantityUnit,
                                  )}
                                </span>
                              </div>
                              <span className="text-xs text-neutral/60">
                                {formatMovementDate(movement.occurredAt)}
                              </span>
                            </div>
                          </Checkbox>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {selectedMovements.length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-neutral/75">
                    {t.lotsListView.allocation.selectedMovements(
                      selectedMovements.length,
                    )}
                  </div>
                  <div className="max-h-[200px] space-y-2 overflow-y-auto">
                    {selectedMovements.map((movement) => {
                      const alloc = allocations.find(
                        (a) => a.movementId === movement.id,
                      );
                      return (
                        <MovementAllocationRow
                          key={movement.id}
                          originTransactionType={movement.originTransactionType}
                          originTransactionItemId={
                            movement.originTransactionItemId
                          }
                          itemName={movement.itemName}
                          occurredAt={movement.occurredAt}
                          quantity={alloc?.quantity ?? 0}
                          maxQuantity={movement.unallocatedQuantity}
                          quantityUnit={movement.quantityUnit}
                          onChange={(qty) =>
                            handleQuantityChange(movement.id, qty)
                          }
                          onRemove={() => handleRemoveMovement(movement.id)}
                          isDisabled={isSubmitting}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.lotsListView.dialog.cancel}
          </Button>
          <Button
            icon={<LucidePlus />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
            isLoading={isSubmitting}
          >
            {t.lotsListView.allocation.addToLot}
          </Button>
        </div>
      }
    />
  );
};
